import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Loader, AlertCircle, Images, Upload, X, Pencil, Check, Trash2, MessageSquare } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { eventTeamApiService } from '../../services/eventTeamApi';
import { useEventMediaUrl } from '../../hooks/useEventMediaUrl';
import type { EventMedia } from '../../types/eventTeam';
import type { EventTeamPerms } from '../../utils/eventTeamPermissions';
import { EventMediaThumbnail } from './EventMediaThumbnail';
import { CreatorProfile } from '../CreatorProfile';
import { ConfirmationModal } from '../ConfirmationModal';
import { SectionHeader } from '../ui/SectionHeader';
import { EmptyState } from '../ui/EmptyState';
import { SkeletonLoader } from '../SkeletonLoader';

export interface EventGallerySectionProps {
  targetType: 'event' | 'project';
  targetId: string;
  perms: EventTeamPerms;
  refreshNonce?: number;
  onChanged?: () => void;
}

function Lightbox({ media, onClose }: { media: EventMedia; onClose: () => void }) {
  const { url, loading } = useEventMediaUrl(media.id);
  return createPortal(
    <div className="modal-overlay z-[9999]" onClick={onClose}>
      <button
        type="button"
        className="btn-icon absolute top-4 right-4 text-white/70 hover:text-white"
        onClick={onClose}
        title="Close"
        aria-label="Close image"
      >
        <X className="w-6 h-6" />
      </button>
      <div className="max-w-[95vw] max-h-[90vh] flex flex-col items-center gap-2" onClick={(e) => e.stopPropagation()}>
        {loading ? (
          <Loader className="w-8 h-8 animate-spin text-white/70" />
        ) : url ? (
          <img src={url} alt={media.caption || media.filename} className="max-w-full max-h-[80vh] object-contain rounded-lg" />
        ) : (
          <p className="text-white/70">Failed to load image</p>
        )}
        <div className="text-center">
          <p className="text-sm text-white">{media.caption || media.filename}</p>
          <p className="text-xs text-white/50">
            {media.filename} · {(media.size / 1024).toFixed(0)} KB
          </p>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/**
 * Image gallery (posters, screenshots — guide §1.4/§4). Multipart `files*` uploads
 * with an optional shared caption; comment-attached images also appear here
 * (they share `event_media`).
 */
export const EventGallerySection = ({
  targetType,
  targetId,
  perms,
  refreshNonce = 0,
  onChanged,
}: EventGallerySectionProps) => {
  const [images, setImages] = useState<EventMedia[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [caption, setCaption] = useState('');
  const [uploading, setUploading] = useState(false);

  const [editingCaptionId, setEditingCaptionId] = useState<string | null>(null);
  const [captionDraft, setCaptionDraft] = useState('');
  const [savingCaption, setSavingCaption] = useState(false);

  const [lightboxMedia, setLightboxMedia] = useState<EventMedia | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<EventMedia | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const data = await eventTeamApiService.listImages(targetType, targetId, 1, 200);
      setImages(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load gallery');
    } finally {
      setLoading(false);
    }
  }, [targetId, targetType]);

  useEffect(() => {
    setLoading(true);
    void load();
  }, [load]);

  useEffect(() => {
    if (refreshNonce === 0) return;
    void load();
  }, [refreshNonce, load]);

  const handleFilePick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []).filter((f) => f.type.startsWith('image/'));
    if (files.length !== (e.target.files?.length ?? 0)) {
      alert('Only image files are accepted (image/* content types).');
    }
    if (files.length > 0) setPendingFiles((prev) => [...prev, ...files]);
    e.target.value = '';
  };

  const handleUpload = async () => {
    if (pendingFiles.length === 0) return;
    setUploading(true);
    try {
      const formData = new FormData();
      if (caption.trim()) formData.append('caption', caption.trim());
      if (pendingFiles.length === 1) {
        formData.append('file', pendingFiles[0]);
      } else {
        pendingFiles.forEach((f, i) => formData.append(`files[${i}]`, f));
      }
      await eventTeamApiService.uploadImages(targetType, targetId, formData);
      setPendingFiles([]);
      setCaption('');
      await load();
      onChanged?.();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to upload images');
    } finally {
      setUploading(false);
    }
  };

  const handleSaveCaption = async (media: EventMedia) => {
    setSavingCaption(true);
    try {
      await eventTeamApiService.updateImageCaption(media.id, captionDraft.trim() || null);
      setEditingCaptionId(null);
      await load();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to update caption');
    } finally {
      setSavingCaption(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await eventTeamApiService.deleteImage(deleteTarget.id);
      setDeleteTarget(null);
      await load();
      onChanged?.();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete image');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3" aria-busy="true">
        {Array.from({ length: 4 }).map((_, i) => (
          <SkeletonLoader key={i} className="w-full aspect-square rounded-lg" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <SectionHeader title="Gallery" icon={Images} iconClassName="text-sky-400" count={images.length} className="mb-0" />

      {error && (
        <div className="alert alert-danger" role="alert">
          <AlertCircle />
          <span className="flex-1">{error}</span>
        </div>
      )}

      {perms.canCreate && (
        <div className="subcard p-3 space-y-2">
          <div className="flex items-center gap-2 flex-wrap">
            <label className="glass-button-secondary btn-xs cursor-pointer">
              <Upload className="w-3.5 h-3.5" />
              Pick images
              <input type="file" accept="image/*" multiple className="hidden" onChange={handleFilePick} />
            </label>
            <input
              type="text"
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              placeholder="Caption (applies to all files in this upload)"
              className="glass-input text-sm flex-1 min-w-[200px]"
            />
            <button
              type="button"
              onClick={() => void handleUpload()}
              disabled={uploading || pendingFiles.length === 0}
              className="glass-button-sm"
            >
              {uploading ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
              Upload {pendingFiles.length > 0 ? `(${pendingFiles.length})` : ''}
            </button>
          </div>
          {pendingFiles.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {pendingFiles.map((file, i) => (
                <span key={`${file.name}-${i}`} className="chip chip-removable gap-1">
                  {file.name}
                  <button
                    type="button"
                    onClick={() => setPendingFiles((prev) => prev.filter((_, j) => j !== i))}
                    className="hover:text-red-300"
                    title="Remove file"
                    aria-label={`Remove ${file.name}`}
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {images.length === 0 && !error ? (
        <EmptyState bare compact icon={Images} title="No images" description={perms.canCreate ? 'Upload posters or screenshots to build the gallery.' : undefined} />
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
          {images.map((media) => (
            <div key={media.id} className="space-y-1">
              <EventMediaThumbnail
                media={media}
                className="w-full aspect-square"
                onClick={() => setLightboxMedia(media)}
              />
              {editingCaptionId === media.id ? (
                <div className="flex items-center gap-1">
                  <input
                    type="text"
                    value={captionDraft}
                    onChange={(e) => setCaptionDraft(e.target.value)}
                    className="glass-input text-xs flex-1 py-1"
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={() => setEditingCaptionId(null)}
                    className="btn-icon btn-icon-sm"
                    title="Cancel"
                    aria-label="Cancel caption edit"
                  >
                    <X className="w-3 h-3" />
                  </button>
                  <button
                    type="button"
                    onClick={() => void handleSaveCaption(media)}
                    disabled={savingCaption}
                    className="btn-icon btn-icon-sm text-accent-300"
                    title="Save caption"
                    aria-label="Save caption"
                  >
                    {savingCaption ? <Loader className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                  </button>
                </div>
              ) : (
                <div className="flex items-start justify-between gap-1">
                  <div className="min-w-0">
                    <p className="text-[11px] text-glass-300 truncate" title={media.caption || media.filename}>
                      {media.caption || media.filename}
                    </p>
                    <p className="text-[10px] text-glass-500 flex items-center gap-1 flex-wrap tabular-nums">
                      <CreatorProfile creatorId={media.uploader_id} inline />
                      · {formatDistanceToNow(new Date(media.created_at), { addSuffix: true })}
                      {media.source_comment_id && (
                        <span className="inline-flex items-center gap-0.5 text-glass-400" title="Attached via a comment">
                          <MessageSquare className="w-2.5 h-2.5" />
                        </span>
                      )}
                    </p>
                  </div>
                  <div className="flex items-center flex-shrink-0">
                    {perms.canCreate && (
                      <button
                        onClick={() => {
                          setEditingCaptionId(media.id);
                          setCaptionDraft(media.caption ?? '');
                        }}
                        className="btn-icon btn-icon-sm"
                        title="Edit caption"
                        aria-label="Edit caption"
                      >
                        <Pencil className="w-3 h-3" />
                      </button>
                    )}
                    {perms.canAdmin && (
                      <button
                        type="button"
                        onClick={() => setDeleteTarget(media)}
                        className="btn-icon btn-icon-sm hover:text-red-300"
                        title="Delete image (admin)"
                        aria-label="Delete image"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {lightboxMedia && <Lightbox media={lightboxMedia} onClose={() => setLightboxMedia(null)} />}

      {deleteTarget && (
        <ConfirmationModal
          isOpen={!!deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onConfirm={() => void handleDelete()}
          title="Delete Image"
          message={`Delete "${deleteTarget.caption || deleteTarget.filename}"? The file will be removed from storage.`}
          confirmText="Delete"
          isLoading={deleting}
        />
      )}
    </div>
  );
};
